import { ConversationUser, IConversationUser } from "./Conversation";
import { DateAdapter } from "@/infrastructure/adapters/dayJs/DayJs.adapter";
import { DateService } from "@/core/services/Date.service";

export interface IMessage {
  conversationId: number;
  requestUniqueKey: string;
  createdAt: string | null;
  id: number | null;
  updatedAt: string | null;
  contentHtml: string | null;
  attachments: IAttachment[] | null;
  isOwner: boolean;
  sender: IConversationUser;
  isError: boolean;
}

export interface IAttachment {
  fileName: string;
  fileDisplayName: string;
  filePath: string;
  fileSize: number;
  fileMimeType: string;
  id: number;
  messageId: number;
}

export class ConversationMessage implements IMessage {
  private readonly _conversationId: number;
  private readonly _requestUniqueKey: string;
  private readonly _createdAt: string | null;
  private _id?: number | null;
  private readonly _updatedAt?: string | null;
  private readonly _contentHtml?: string | null;
  private _attachments?: IAttachment[] | null;
  private readonly _isOwner: boolean;
  private readonly _sender: IConversationUser;
  private _isError?: boolean;

  constructor(data: IMessage) {
    this._conversationId = data.conversationId;
    this._requestUniqueKey = data.requestUniqueKey;
    this._createdAt = data.createdAt ?? null;
    this._id = data.id;
    this._updatedAt = data.updatedAt;
    this._contentHtml = data.contentHtml;
    this._attachments = data.attachments?.map(attachment => new Attachment(attachment));
    this._isOwner = data.isOwner;
    this._sender = new ConversationUser(data.sender);
    this._isError = data.isError;
  }

  get requestUniqueKey(): string {
    return this._requestUniqueKey;
  }

  get sender(): IConversationUser {
    return this._sender;
  }

  get conversationId(): number {
    return this._conversationId;
  }

  get createdAt(): string | null {
    return this._createdAt;
  }

  public createdAtFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._createdAt ?? "", format);
  }

  get id(): number | null {
    return this._id ?? null;
  }

  get updatedAt(): string | null {
    return this._updatedAt ?? null;
  }

  get contentHtml(): string | null {
    return this._contentHtml ?? null;
  }

  get attachments(): IAttachment[] | null {
    return this._attachments ?? null;
  }

  get isOwner(): boolean {
    return this._isOwner;
  }

  get isError(): boolean {
    return this._isError ?? false;
  }

  public updateMessageId(messageId: number): void {
    this._id = messageId;
  }

  public updateAttachments(attachments: IAttachment[]): void {
    this._attachments = attachments;
  }

  public updateIsError(isError: boolean): void {
    this._isError = isError;
  }
}

export class Attachment implements IAttachment {
  private readonly _id: number;
  private readonly _messageId: number;
  private readonly _fileName: string;
  private readonly _fileDisplayName: string;
  private readonly _filePath: string;
  private readonly _fileSize: number;
  private readonly _fileMimeType: string;

  constructor(data: IAttachment) {
    this._fileName = data.fileName;
    this._fileDisplayName = data.fileDisplayName;
    this._filePath = data.filePath;
    this._fileSize = data.fileSize;
    this._fileMimeType = data.fileMimeType;
    this._id = data.id;
    this._messageId = data.messageId;
  }

  get fileName(): string {
    return this._fileName;
  }

  get fileDisplayName(): string {
    return this._fileDisplayName;
  }

  get filePath(): string {
    return this._filePath;
  }

  get fileSize(): number {
    return this._fileSize;
  }

  get fileMimeType(): string {
    return this._fileMimeType;
  }

  get id(): number {
    return this._id;
  }

  get messageId(): number {
    return this._messageId;
  }
}
